export const WEBHOOK_CATEGORIES = {
  SUBMISSIONS: 'submissions',
  ENGAGEMENT: 'engagement',
};

// Automation platforms that consume a plain incoming webhook. No OAuth app is
// involved — each of these just hands the user a URL to paste here — but almost
// nobody realises "Webhooks" is how you connect Zapier, so surface them by name.
// `match` detects an already-pasted URL so the chip can show as connected.
export const AUTOMATION_PRESETS = [
  {
    id: 'zapier',
    label: 'Zapier',
    match: /(^|\.)zapier\.com\//i,
    docsUrl: 'https://zapier.com/apps/webhook/integrations',
    steps: ['In Zapier, create a Zap and pick "Webhooks by Zapier" → "Catch Hook" as the trigger.', 'Copy the custom webhook URL Zapier shows you.', 'Paste it above, choose your events and save.'],
  },
  {
    id: 'make',
    label: 'Make',
    match: /(^|\.)make\.com\/|integromat\.com\//i,
    docsUrl: 'https://www.make.com/en/help/tools/webhooks',
    steps: ['In Make, add a "Custom webhook" module to a new scenario.', 'Click "Add" and copy the generated address.', 'Paste it above, choose your events and save.'],
  },
  {
    id: 'n8n',
    label: 'n8n',
    match: /(^|\.)n8n\.(cloud|io)\//i,
    docsUrl: 'https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/',
    steps: ['In n8n, add a "Webhook" node as the trigger.', 'Copy its Production URL.', 'Paste it above, choose your events and save.'],
  },
];

// Maps EventType -> API field name in the `webhook` object
export const WEBHOOK_EVENTS = [
  // === SUBMISSIONS (26) ===
  { key: 'isHeaderSubscribe', eventType: 'SubmitHeaderSubscribe', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isHeaderMessage', eventType: 'SubmitHeaderMessage', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isEmailSubmit', eventType: 'SubmitDataCollect', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isFormSubmit', eventType: 'SubmitForm', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isCartSubmit', eventType: 'SubmitShop', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isShopPreorder', eventType: 'SubmitShopPreorder', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isDigitalSubmit', eventType: 'SubmitDigital', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isAppointmentSubmit', eventType: 'SubmitAppointment', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isRequestSubmit', eventType: 'SubmitService', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isEventSubmit', eventType: 'SubmitEvent', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isCourseSubmit', eventType: 'SubmitCourse', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isDonationSubmit', eventType: 'SubmitDonation', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isMembershipSubmit', eventType: 'SubmitMembership', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isBlogSubmit', eventType: 'SubmitBlog', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isCouponSubmit', eventType: 'SubmitCoupon', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isTipSubmit', eventType: 'SubmitTip', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isLinkSubmit', eventType: 'SubmitLink', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isProductMessage', eventType: 'SubmitProductMessage', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isPodcastSubmit', eventType: 'SubmitPodcast', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isBookingSubmit', eventType: 'SubmitBooking', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isRentalSubmit', eventType: 'SubmitRental', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isSingleProduct', eventType: 'SubmitSingleProduct', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isDiscountOrder', eventType: 'SubmitDiscount', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isListingRequest', eventType: 'SubmitListing', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isContentUnlock', eventType: 'SubmitUnlock', category: WEBHOOK_CATEGORIES.SUBMISSIONS },
  { key: 'isChatAi', eventType: 'SubmitChatAi', category: WEBHOOK_CATEGORIES.SUBMISSIONS },

  // === ENGAGEMENT (12) ===
  { key: 'isLinkClick', eventType: 'ClickLink', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isContactClick', eventType: 'ClickContact', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isCartAdd', eventType: 'AddToCart', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isSingleProductToCart', eventType: 'AddToCartSingleProduct', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isWishlist', eventType: 'AddToWishlist', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isInitiateCheckout', eventType: 'InitiateCheckout', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isFileDownload', eventType: 'DownloadFile', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isMusicDownload', eventType: 'DownloadMusic', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isCopyCoupon', eventType: 'CopyCoupon', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isCopyDiscount', eventType: 'CopyDiscount', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isQrCodeScan', eventType: 'QR_ScanQrCode', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
  { key: 'isPageVisit', eventType: 'VisitPage', category: WEBHOOK_CATEGORIES.ENGAGEMENT },
];

export const TOTAL_EVENTS = WEBHOOK_EVENTS.length;

export const SUBMISSIONS_EVENTS = WEBHOOK_EVENTS.filter((e) => e.category === WEBHOOK_CATEGORIES.SUBMISSIONS);
export const ENGAGEMENT_EVENTS = WEBHOOK_EVENTS.filter((e) => e.category === WEBHOOK_CATEGORIES.ENGAGEMENT);

// Build default webhookEvents object (all false)
export const DEFAULT_WEBHOOK_EVENTS = WEBHOOK_EVENTS.reduce((acc, event) => {
  acc[event.key] = false;
  return acc;
}, {});
